import api from './api';

/**
 * Ungelesen-Zähler für die Startseiten-Tabs Feed / Für dich / Chat
 * (Marcel 2026-09-03, wie in der App). Der Zeitpunkt des letzten Öffnens
 * liegt im localStorage, das Backend zählt nur:
 * GET /api/home/unread-counts?feedSince&forYouSince&chatSince
 */
export type UnreadTab = 'feed' | 'forYou' | 'chat';

export interface HomeUnreadCounts {
  feed: number;
  forYou: number;
  chat: number;
  cap: number;
}

export const UNREAD_BADGE_CAP = 99;
export const EMPTY_UNREAD: HomeUnreadCounts = { feed: 0, forYou: 0, chat: 0, cap: UNREAD_BADGE_CAP };

const SEEN_KEYS: Record<UnreadTab, string> = {
  feed: 'dc-home-seen-feed',
  forYou: 'dc-home-seen-for-you',
  chat: 'dc-home-seen-chat',
};

/** PURE: Badge nur bei > 0 */
export function shouldShowBadge(count: number | null | undefined): boolean {
  return typeof count === 'number' && Number.isFinite(count) && count > 0;
}

/** PURE: „99+" ab Cap, nie negativ */
export function formatBadgeCount(count: number, cap = UNREAD_BADGE_CAP): string {
  if (!Number.isFinite(count) || count <= 0) return '0';
  return count > cap ? `${cap}+` : String(Math.floor(count));
}

/** PURE + defensiv: Backend-Antwort → Zähler (fehlend/kaputt → 0) */
export function parseUnreadCounts(raw: any): HomeUnreadCounts {
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
  const cap = typeof raw?.cap === 'number' && raw.cap > 0 ? raw.cap : UNREAD_BADGE_CAP;
  return { feed: num(raw?.feed), forYou: num(raw?.forYou), chat: num(raw?.chat), cap };
}

/** PURE: Query-String ohne fehlende since-Werte */
export function buildUnreadQuery(since: Partial<Record<UnreadTab, number | null>>): string {
  const params = new URLSearchParams();
  if (since.feed) params.set('feedSince', String(since.feed));
  if (since.forYou) params.set('forYouSince', String(since.forYou));
  if (since.chat) params.set('chatSince', String(since.chat));
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function readSeen(tab: UnreadTab): number | null {
  try {
    const raw = window.localStorage.getItem(SEEN_KEYS[tab]);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function writeSeen(tab: UnreadTab, ms: number): void {
  try {
    window.localStorage.setItem(SEEN_KEYS[tab], String(ms));
  } catch {
    /* localStorage nicht verfügbar */
  }
}

/** Erststart: fehlende Zeitstempel auf jetzt setzen → kein Badge beim ersten Öffnen */
export function ensureSeenInitialized(nowMs = Date.now()): void {
  (['feed', 'forYou', 'chat'] as UnreadTab[]).forEach((tab) => {
    if (readSeen(tab) == null) writeSeen(tab, nowMs);
  });
}

const homeUnreadService = {
  /** Zähler laden; Fehler → null (Aufrufer behält den letzten Stand) */
  async fetch(since: Partial<Record<UnreadTab, number | null>>): Promise<HomeUnreadCounts | null> {
    try {
      const res = await api.get(`/api/home/unread-counts${buildUnreadQuery(since)}`);
      return parseUnreadCounts(res?.data);
    } catch (err) {
      console.debug('homeUnreadService: Zähler konnten nicht geladen werden', err);
      return null;
    }
  },
};

export default homeUnreadService;
